import React, {useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useNavigate} from 'react-router-dom';
import {BellIcon, CheckCheckIcon, XIcon} from './icons';
import Spinner from './Spinner';
import notificationStore, {NotificationItem} from '../store/NotificationStore';
import useDialogA11y from '../hooks/useDialogA11y';
import NotificationItemRow from './NotificationItemRow';
import authService from '../service/AuthService';
import {resolveDeepLink} from '../support/deepLink';

/**
 * Колокольчик центра уведомлений в шапке (CTHS-43). Badge непрочитанных (AC1), выпадающий список
 * последних N с состояниями loading/empty/error (AC3), mark-read по клику и «прочитать все» (AC2).
 * Live WS-события приходят в общий {@link notificationStore} (дедуп по id) — компонент только
 * реактивно их отображает. Тач-зоны ≥44px, light/dark — семантические Tailwind-токены.
 */
const NotificationBell: React.FC = observer(() => {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    // AC1: badge берётся с сервера при монтировании — переживает перезагрузку страницы.
    useEffect(() => {
        if (authService.isAuthenticated()) {
            notificationStore.init();
        }
    }, []);

    // Реконсиляция при каждом открытии: подтягиваем список и актуальный счётчик с сервера.
    useEffect(() => {
        if (open) {
            notificationStore.loadRecent();
            notificationStore.loadUnreadCount();
        }
    }, [open]);

    // Закрытие по клику вне (доступность выпадашки).
    useEffect(() => {
        if (!open) {
            return;
        }
        const onDocClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
        };
    }, [open]);

    // Escape и возврат фокуса на колокольчик — общий контракт диалогов (QA-16).
    // Фокус НЕ запираем: панель — поповер, а не модальное окно; она не перекрывает
    // страницу скримом, и выход по Tab дальше по шапке ожидаем.
    useDialogA11y(panelRef, () => setOpen(false), {active: open, trapFocus: false});

    const activate = async (item: NotificationItem): Promise<void> => {
        await notificationStore.markRead(item);
        setOpen(false);
        const target = resolveDeepLink(item.deepLink);
        if (target) {
            navigate(target);
        }
    };

    const unread = notificationStore.unreadCount;
    const badgeLabel = unread > 99 ? '99+' : String(unread);

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-label={unread > 0 ? `Уведомления, непрочитанных: ${unread}` : 'Уведомления'}
                aria-haspopup="dialog"
                aria-expanded={open}
                data-testid="notification-bell"
                className="relative p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
                <BellIcon className="w-5 h-5"/>
                {unread > 0 && (
                    <span
                        data-testid="notification-badge"
                        className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-[18px] text-center"
                    >
                        {badgeLabel}
                    </span>
                )}
            </button>

            {open && (
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-label="Центр уведомлений"
                    tabIndex={-1}
                    data-testid="notification-panel"
                    className="fw-popover origin-top-right outline-none absolute right-0 mt-2 w-[calc(100vw-2rem)] max-w-sm sm:w-96 max-h-[70vh] flex flex-col rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-ink-800 shadow-card-hover z-40 overflow-hidden"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/10">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Уведомления</h3>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => notificationStore.markAllRead()}
                                disabled={unread === 0}
                                data-testid="mark-all-read"
                                className="inline-flex items-center gap-1 px-2 py-1 min-h-[36px] rounded-lg text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                <CheckCheckIcon className="w-4 h-4"/>
                                <span className="hidden sm:inline">Прочитать все</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label="Закрыть"
                                className="p-1 min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5"
                            >
                                <XIcon className="w-4 h-4"/>
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {notificationStore.loadingRecent ? (
                            <div
                                data-testid="notification-loading"
                                className="flex items-center justify-center py-10"
                                role="status"
                                aria-live="polite"
                            >
                                <Spinner/>
                            </div>
                        ) : notificationStore.recentError ? (
                            <div data-testid="notification-error" className="px-4 py-10 text-center" role="alert">
                                <p className="text-sm text-gray-600 dark:text-gray-300">Не удалось загрузить уведомления.</p>
                                <button
                                    type="button"
                                    onClick={() => notificationStore.loadRecent()}
                                    className="mt-3 inline-flex px-3 py-2 min-h-[40px] rounded-lg text-sm font-medium text-white bg-ink-900 hover:bg-ink-800 dark:bg-white/10 dark:hover:bg-white/20 transition-colors"
                                >
                                    Повторить
                                </button>
                            </div>
                        ) : notificationStore.recent.length === 0 ? (
                            <div data-testid="notification-empty" className="px-4 py-10 text-center">
                                <BellIcon className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600"/>
                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Уведомлений пока нет</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-gray-100 dark:divide-white/5">
                                {notificationStore.recent.map(item => (
                                    <li key={item.id}>
                                        <NotificationItemRow item={item} onActivate={activate}/>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="border-t border-gray-100 dark:border-white/10">
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                navigate('/notifications');
                            }}
                            data-testid="all-notifications-link"
                            className="w-full px-4 py-3 min-h-[44px] text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                            Все уведомления
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});

export default NotificationBell;
